import { useMemo, useState } from 'react';
import { Button } from '../components/primitives/Button';
import { EditorShell } from '../components/primitives/EditorShell';
import { HistoryDrawer } from '../components/HistoryDrawer';
import type {
  ActivityLine,
  UseVersionHistoryVM
} from '../lib/vm/useVersionHistoryVM';
import type { VersionSnapshot } from '../lib/models/versionHistory';

/**
 * /__test/history-drawer — Playwright-only test harness for the
 * HistoryDrawer primitive. Mounts the drawer open inside EditorShell
 * (matches production layout). The drawer creates its own VM (no `vm` prop)
 * and fetches from the Dev Supabase backend using the fixture subject.
 *
 * Append `?mock=1` to render the drawer with a static populated VM (no
 * backend round-trip) — used for visual review of the Versions table /
 * Activity list layout without seeded data.
 */

function makeMockSnapshot(
  n: number,
  minutesAgo: number,
  body: string
): VersionSnapshot {
  return {
    id: `mock-snap-${n}`,
    subjectKind: 'document',
    subjectId: 'doc-fixture',
    versionNumber: n,
    title: 'Quarterly planning brief',
    body,
    snapshotAt: new Date(Date.now() - minutesAgo * 60_000).toISOString(),
    snapshotBy: `mock-user-${n}`,
    sourceVersionId: null
  };
}

const MOCK_SNAPSHOTS: VersionSnapshot[] = [
  makeMockSnapshot(
    4,
    12,
    '# Quarterly planning brief\n\nUpdated scope for Q3 with the new staffing plan and the revised launch milestones.'
  ),
  makeMockSnapshot(
    3,
    180,
    '# Quarterly planning brief\n\nDraft scope for Q3 — staffing TBD.'
  ),
  makeMockSnapshot(2, 1500, '# Quarterly planning brief\n\nInitial outline.'),
  makeMockSnapshot(1, 4320, '# Quarterly planning brief\n\nEmpty stub.')
];

const MOCK_ACTIVITY_LINES: ActivityLine[] = [
  {
    id: 'mock-evt-3',
    eventType: 'body_changed',
    at: new Date(Date.now() - 12 * 60_000).toISOString(),
    text: 'Dana Whitfield rewrote the body to add the staffing plan'
  },
  {
    id: 'mock-evt-2',
    eventType: 'title_changed',
    at: new Date(Date.now() - 180 * 60_000).toISOString(),
    text: 'Sam Okonkwo renamed the document to "Quarterly planning brief"'
  },
  {
    id: 'mock-evt-1',
    eventType: 'property_changed',
    at: new Date(Date.now() - 4320 * 60_000).toISOString(),
    text: 'Lane Fries set the owner to the Planning team'
  }
];

const MOCK_AUTHOR_HANDLES: Record<string, string> = {
  'mock-user-4': 'Dana Whitfield',
  'mock-user-3': 'Sam Okonkwo',
  'mock-user-2': 'Priya Raman',
  'mock-user-1': 'Lane Fries'
};

type MockModalKind = 'none' | 'view' | 'diff' | 'restore';

/**
 * A stateful in-memory VM for the test harness — the View / Diff / Restore
 * modals actually open so the wide-modal layout can be reviewed without a
 * backend round-trip.
 */
function useMockVersionHistoryVM(): UseVersionHistoryVM {
  const [modal, setModal] = useState<MockModalKind>('none');
  const [modalVersionId, setModalVersionId] = useState<string | null>(null);

  const modalSnapshot = useMemo(
    () => MOCK_SNAPSHOTS.find((s) => s.id === modalVersionId) ?? null,
    [modalVersionId]
  );

  return {
    status: 'ready',
    snapshots: MOCK_SNAPSHOTS,
    activityLines: MOCK_ACTIVITY_LINES,
    authorHandles: MOCK_AUTHOR_HANDLES,
    modal,
    modalSnapshot,
    isRestoring: false,
    restoreError: null,
    activityFilter: 'all',
    visibleActivityLines: MOCK_ACTIVITY_LINES,
    activityFilterOptions: [
      { value: 'all', label: 'All events' },
      { value: 'title_changed', label: 'Title changed' },
      { value: 'body_changed', label: 'Body changed' },
      { value: 'property_changed', label: 'Property changed' }
    ],
    authorFor: (snapshot) =>
      snapshot.snapshotBy
        ? (MOCK_AUTHOR_HANDLES[snapshot.snapshotBy] ?? 'Unknown')
        : 'Unknown',
    actions: {
      reload: () => {},
      openView: (id) => {
        setModalVersionId(id);
        setModal('view');
      },
      openDiff: (id) => {
        setModalVersionId(id);
        setModal('diff');
      },
      openRestore: (id) => {
        setModalVersionId(id);
        setModal('restore');
      },
      closeModal: () => {
        setModal('none');
        setModalVersionId(null);
      },
      confirmRestore: async () => true,
      setActivityFilter: () => {}
    }
  };
}

export function TestHistoryDrawer() {
  const [open, setOpen] = useState(true);

  const useMock = useMemo(
    () => new URLSearchParams(window.location.hash.split('?')[1] ?? '').has(
      'mock'
    ),
    []
  );
  const mockVm = useMockVersionHistoryVM();

  return (
    <section
      className="wid-page"
      data-status="ready"
      data-testid="history-drawer-harness"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh'
      }}
    >
      <header
        style={{
          padding: 'var(--space-4) var(--space-8)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)'
        }}
      >
        <h1 style={{ fontSize: 'var(--text-h1)', margin: 0 }}>
          HistoryDrawer primitive — test harness
        </h1>

        <Button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="probe-open-drawer"
        >
          Open drawer
        </Button>

        <section
          aria-label="probes"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
            fontSize: 'var(--text-body-sm)',
            fontFamily: 'var(--font-mono)'
          }}
        >
          <div>
            open:&nbsp;
            <span data-testid="probe-open">{open ? 'true' : 'false'}</span>
          </div>
        </section>
      </header>

      <EditorShell
        data-testid="history-drawer-harness-shell"
        data-rail-open={open ? 'true' : 'false'}
        style={{ flex: 1, minHeight: 480 }}
      >
        <HistoryDrawer
          subjectKind="document"
          subjectId="doc-fixture"
          open={open}
          onClose={() => setOpen(false)}
          liveTitle="Quarterly planning brief"
          liveBody="# Quarterly planning brief

Updated scope for Q3 with the new staffing plan, the revised launch milestones, and the agreed budget envelope for the campaign."
          vm={useMock ? mockVm : undefined}
        />
      </EditorShell>
    </section>
  );
}
